import React, { useEffect, useState, useMemo } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import {
  Envelope,
  EnvelopeSimple,
  WarningCircle,
  CheckCircle,
  ArrowsClockwise,
  PaperPlaneTilt,
  Funnel,
  CaretRight,
  TestTube,
  ChartLineUp,
  XCircle,
} from '@phosphor-icons/react';

const StatusBadge = ({ status }) => {
  const variants = {
    sent: 'bg-green-50 text-green-700 border-green-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
  };
  const Icon = status === 'sent' ? CheckCircle : status === 'failed' ? XCircle : Envelope;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs border rounded-none ${variants[status] || 'bg-gray-50 text-gray-700 border-gray-200'}`}
      data-testid={`status-badge-${status}`}
    >
      <Icon className="w-3 h-3" weight="fill" />
      {status || 'unknown'}
    </span>
  );
};

const Notifications = () => {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [selected, setSelected] = useState(null);
  const [resending, setResending] = useState(null);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      params.set('limit', '300');
      const [{ data: s }, { data: l }] = await Promise.all([
        api.get('/notification-logs/stats'),
        api.get(`/notification-logs?${params.toString()}`),
      ]);
      setStats(s);
      setLogs(l || []);
    } catch (e) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const filteredLogs = useMemo(() => {
    if (!search.trim()) return logs;
    const q = search.toLowerCase();
    return logs.filter(
      (l) =>
        (l.recipient || '').toLowerCase().includes(q) ||
        (l.subject || '').toLowerCase().includes(q) ||
        (l.template_name || '').toLowerCase().includes(q) ||
        (l.last_error || '').toLowerCase().includes(q),
    );
  }, [logs, search]);

  const handleTestSMTP = async (e) => {
    e?.preventDefault();
    if (!testEmail) return;
    setTestSending(true);
    setTestResult(null);
    try {
      const { data } = await api.post('/notification-logs/test-smtp', { to: testEmail });
      setTestResult(data);
      if (data.ok) {
        toast.success(`SMTP test email sent to ${testEmail}`);
      } else {
        toast.error(`SMTP test failed: ${data.error || 'unknown'}`);
      }
      fetchData();
    } catch (err) {
      setTestResult({ ok: false, error: formatApiError(err) });
      toast.error(formatApiError(err));
    } finally {
      setTestSending(false);
    }
  };

  const handleResend = async (logId) => {
    setResending(logId);
    try {
      const { data } = await api.post(`/notification-logs/${logId}/resend`);
      if (data.ok) {
        toast.success('Resent successfully');
      } else {
        toast.error('Resend failed (still failing — check SMTP)');
      }
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResending(null);
    }
  };

  return (
    <AdminLayout title="Notifications & Email Delivery">
      <div className="space-y-6 fade-in" data-testid="notifications-page">
        {/* SMTP health banner */}
        {stats && (
          <Card
            className={`border-l-4 ${
              !stats.smtp_configured
                ? 'border-l-red-500'
                : stats.recent_failures_24h > 0
                ? 'border-l-orange-500'
                : 'border-l-green-500'
            }`}
            data-testid="smtp-health-banner"
          >
            <CardContent className="pt-5 pb-5">
              <div className="flex items-start gap-3">
                {!stats.smtp_configured ? (
                  <WarningCircle className="w-6 h-6 text-red-600 mt-0.5 shrink-0" weight="fill" />
                ) : stats.recent_failures_24h > 0 ? (
                  <WarningCircle className="w-6 h-6 text-orange-500 mt-0.5 shrink-0" weight="fill" />
                ) : (
                  <CheckCircle className="w-6 h-6 text-green-600 mt-0.5 shrink-0" weight="fill" />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-[#0A192F]">
                    {!stats.smtp_configured
                      ? 'SMTP NOT configured'
                      : stats.recent_failures_24h > 0
                      ? `${stats.recent_failures_24h} email failures in the last 24h`
                      : 'Email delivery healthy'}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {stats.smtp_host ? (
                      <>
                        SMTP via <code className="text-xs bg-gray-100 px-1 py-0.5">{stats.smtp_host}:{stats.smtp_port}</code>
                        {' '}as <code className="text-xs bg-gray-100 px-1 py-0.5">{stats.smtp_user}</code>
                      </>
                    ) : (
                      'No SMTP_HOST configured in environment.'
                    )}
                  </p>
                  {stats.top_errors_24h?.length > 0 && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-100" data-testid="top-errors">
                      <p className="text-xs font-semibold text-red-700 uppercase tracking-wider">Most common errors (24h)</p>
                      <ul className="mt-1 space-y-1">
                        {stats.top_errors_24h.map((te, i) => (
                          <li key={i} className="text-xs text-red-800 font-mono break-all">
                            <span className="inline-block bg-red-100 px-1.5 py-0.5 mr-2 font-bold">{te.count}×</span>
                            {te.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <Dialog open={testOpen} onOpenChange={setTestOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="rounded-none shrink-0"
                      data-testid="test-smtp-btn"
                    >
                      <TestTube className="w-4 h-4 mr-2" />
                      Test SMTP
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                      <DialogTitle>Send SMTP Diagnostic Email</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleTestSMTP} className="space-y-3 mt-2">
                      <Label className="text-sm">Send a test email to:</Label>
                      <Input
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                        className="rounded-none"
                        data-testid="test-smtp-email"
                      />
                      <Button
                        type="submit"
                        disabled={testSending}
                        className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none w-full"
                        data-testid="test-smtp-submit"
                      >
                        {testSending ? 'Sending…' : 'Send Test'}
                      </Button>
                      {testResult && (
                        <div
                          className={`p-3 text-sm ${
                            testResult.ok ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
                          }`}
                          data-testid="test-smtp-result"
                        >
                          {testResult.ok ? (
                            <p>✅ Sent successfully to {testEmail}. Check the inbox.</p>
                          ) : (
                            <>
                              <p className="font-semibold">❌ Send failed:</p>
                              <p className="font-mono text-xs mt-1 break-all">{testResult.error}</p>
                              <p className="text-xs mt-2 text-gray-600">
                                Common causes: wrong SMTP_PASSWORD in `.env`, wrong port (465 vs 587),
                                or special characters in the password not properly quoted.
                              </p>
                            </>
                          )}
                        </div>
                      )}
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="notif-stats-row">
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wider text-gray-500">Total sent</p>
                <p className="text-2xl font-bold text-[#0A192F] mt-1">{stats.total}</p>
                <p className="text-xs text-gray-400 mt-1">{Math.round(stats.success_rate * 100)}% success</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wider text-gray-500">Delivered</p>
                <p className="text-2xl font-bold text-green-700 mt-1">{stats.sent}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wider text-gray-500">Failed</p>
                <p className="text-2xl font-bold text-red-700 mt-1">{stats.failed}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-orange-500">
              <CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wider text-gray-500">Failed in last 24h</p>
                <p className="text-2xl font-bold text-orange-700 mt-1">{stats.recent_failures_24h}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-5 pb-5">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Funnel className="w-4 h-4 text-gray-500" />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-36 rounded-none" data-testid="filter-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input
                placeholder="Search recipient, subject, error…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 min-w-[200px] rounded-none"
                data-testid="search-notifs"
              />
              <Button
                variant="outline"
                onClick={fetchData}
                disabled={refreshing}
                className="rounded-none"
                data-testid="refresh-notifs"
              >
                <ArrowsClockwise className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
              <ChartLineUp className="w-5 h-5 text-[#0055FF]" />
              Delivery Log <span className="text-sm text-gray-400 font-normal">({filteredLogs.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {loading ? (
              <div className="flex items-center justify-center h-24">
                <div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : filteredLogs.length === 0 ? (
              <p className="text-center text-gray-400 py-8 text-sm">No notification logs found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="notif-table">
                  <thead className="border-b">
                    <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                      <th className="py-3 px-2">When</th>
                      <th className="py-3 px-2">Status</th>
                      <th className="py-3 px-2">Template</th>
                      <th className="py-3 px-2">Recipient</th>
                      <th className="py-3 px-2 hidden md:table-cell">Subject</th>
                      <th className="py-3 px-2 hidden lg:table-cell">Error</th>
                      <th className="py-3 px-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log) => (
                      <tr
                        key={log.log_id}
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setSelected(log)}
                        data-testid={`notif-row-${log.log_id}`}
                      >
                        <td className="py-3 px-2 text-xs text-gray-600 whitespace-nowrap">
                          {new Date(log.sent_at).toLocaleString([], {
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                          })}
                        </td>
                        <td className="py-3 px-2"><StatusBadge status={log.status} /></td>
                        <td className="py-3 px-2 text-xs text-gray-700 font-mono">{log.template_name || '—'}</td>
                        <td className="py-3 px-2 text-xs">{log.recipient}</td>
                        <td className="py-3 px-2 hidden md:table-cell text-xs text-gray-700 truncate max-w-xs">{log.subject}</td>
                        <td className="py-3 px-2 hidden lg:table-cell text-xs text-red-700 font-mono truncate max-w-xs">{log.last_error || '—'}</td>
                        <td className="py-3 px-2 text-right">
                          {log.status === 'failed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); handleResend(log.log_id); }}
                              disabled={resending === log.log_id}
                              className="rounded-none text-xs"
                              data-testid={`resend-${log.log_id}`}
                            >
                              {resending === log.log_id ? (
                                <ArrowsClockwise className="w-3 h-3 animate-spin" />
                              ) : (
                                <><PaperPlaneTilt className="w-3 h-3 mr-1" /> Resend</>
                              )}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-[560px]" data-testid="notif-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <EnvelopeSimple className="w-5 h-5 text-[#0055FF]" />
              Notification details
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-gray-500">Status</span>
                <span className="col-span-2"><StatusBadge status={selected.status} /></span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-gray-500">Sent at</span>
                <span className="col-span-2 font-mono text-xs">{new Date(selected.sent_at).toLocaleString()}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-gray-500">Template</span>
                <span className="col-span-2 font-mono text-xs">{selected.template_name}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-gray-500">Recipient</span>
                <span className="col-span-2 break-all">{selected.recipient}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-gray-500">Subject</span>
                <span className="col-span-2 break-words">{selected.subject}</span>
              </div>
              {selected.last_error && (
                <div className="p-3 bg-red-50 border border-red-200">
                  <p className="text-xs font-semibold text-red-800 uppercase tracking-wider">SMTP error</p>
                  <p className="font-mono text-xs text-red-900 mt-1 break-all">{selected.last_error}</p>
                </div>
              )}
              {selected.case_id && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-gray-500">Case</span>
                  <a className="col-span-2 text-[#0055FF] hover:underline" href={`/admin/cases/${selected.case_id}`}>
                    {selected.case_id} <CaretRight className="w-3 h-3 inline" />
                  </a>
                </div>
              )}
              {selected.status === 'failed' && (
                <Button
                  onClick={() => { handleResend(selected.log_id); }}
                  disabled={resending === selected.log_id}
                  className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-none w-full"
                  data-testid="resend-from-detail"
                >
                  {resending === selected.log_id ? 'Resending…' : 'Resend Now'}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default Notifications;
